import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, Clock3, ExternalLink, LoaderCircle, Mail, Map, MapPin, Phone } from 'lucide-react';
import SEO from '../../components/ui/SEO';
import useSiteSettings from '../../hooks/useSiteSettings';

declare global {
  interface Window {
    kakao: any;
  }
}

type MapStatus = 'loading' | 'ready' | 'error';

function useKakaoMap(containerId: string, lat: number, lng: number, address: string) {
  const [status, setStatus] = useState<MapStatus>('loading');
  const mapRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;

    const initialize = () => {
      if (cancelled) return;
      const container = document.getElementById(containerId);
      if (!container) return;

      const position = new window.kakao.maps.LatLng(lat, lng);
      const map = new window.kakao.maps.Map(container, { center: position, level: 4 });
      const marker = new window.kakao.maps.Marker({ position });
      marker.setMap(map);
      new window.kakao.maps.InfoWindow({
        content: `<div style="padding:10px 14px;font-size:13px;font-weight:600;white-space:nowrap">COBRAND<br><span style="font-size:12px;font-weight:400;color:#57534e">${address}</span></div>`,
      }).open(map, marker);
      mapRef.current = map;
      setStatus('ready');
    };

    const load = async () => {
      setStatus('loading');
      try {
        const response = await fetch('/api/settings/kakao/public');
        if (!response.ok) throw new Error();
        const config = await response.json();
        if (!config.enabled || !config.javascript_key) throw new Error();

        if (window.kakao?.maps) {
          window.kakao.maps.load(initialize);
          return;
        }

        const existing = document.getElementById('kakao-map-sdk') as HTMLScriptElement | null;
        const script = existing ?? document.createElement('script');
        if (!existing) {
          script.id = 'kakao-map-sdk';
          script.async = true;
          script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${encodeURIComponent(config.javascript_key)}&autoload=false`;
          document.head.appendChild(script);
        }
        script.addEventListener('load', () => window.kakao.maps.load(initialize), { once: true });
        script.addEventListener('error', () => !cancelled && setStatus('error'), { once: true });
      } catch {
        if (!cancelled) setStatus('error');
      }
    };

    load();
    return () => { cancelled = true; };
  }, [address, containerId, lat, lng]);

  return { status, mapRef };
}

export default function ContactPage() {
  const settings = useSiteSettings();
  const lat = Number(settings.contact_lat);
  const lng = Number(settings.contact_lng);
  const emails = [settings.contact_email, settings.contact_email_secondary].filter(Boolean);
  const { status } = useKakaoMap('kakao-map', lat, lng, settings.contact_address);
  const encodedAddress = encodeURIComponent(settings.contact_address);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="연락처"
        path="/contact"
        description={`COBRAND 연락처. ${settings.contact_address}. 전화: ${settings.contact_phone}. 이메일: ${emails.join(', ')}.`}
        keywords="COBRAND연락처,가방제작문의,코브랜드주소,목동"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: settings.brand_name,
          image: 'https://brand.bagself.com/logo192.png',
          telephone: settings.contact_phone,
          email: settings.contact_email,
          address: { '@type': 'PostalAddress', streetAddress: settings.contact_address, addressCountry: 'KR' },
          geo: { '@type': 'GeoCoordinates', latitude: lat, longitude: lng },
          url: 'https://brand.bagself.com',
        }}
      />

      <header className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-amber-700 text-xs tracking-widest uppercase mb-3">Contact Us</p>
          <h1 className="text-4xl font-bold mb-3">연락처</h1>
          <p className="text-stone-600 text-sm">전문 담당자가 문의 내용을 확인하고 빠르게 답변드립니다.</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <section className="lg:col-span-2 space-y-4" aria-label="연락처 정보">
            <ContactItem icon={Phone} label="전화">
              <a href={`tel:${settings.contact_phone.replace(/-/g, '')}`} className="block text-lg font-bold text-stone-900 hover:text-amber-700">
                {settings.contact_phone}
              </a>
              <p className="text-xs text-stone-600 mt-1">{settings.business_hours}</p>
            </ContactItem>

            <ContactItem icon={Mail} label="이메일">
              {emails.map(email => (
                <a key={email} href={`mailto:${email}`} className="block text-base font-semibold text-stone-800 hover:text-amber-700 break-all">
                  {email}
                </a>
              ))}
              <p className="text-xs text-stone-600 mt-1">1~2 영업일 내 답변</p>
            </ContactItem>

            <ContactItem icon={MapPin} label="주소">
              <p className="text-base font-semibold text-stone-800 leading-relaxed">{settings.contact_address}</p>
              <a href={`https://map.kakao.com/link/search/${encodedAddress}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-amber-700 hover:underline mt-2">
                카카오맵에서 보기 <ExternalLink size={13} aria-hidden="true" />
              </a>
            </ContactItem>

            <section className="border border-stone-200 rounded-lg p-6" aria-labelledby="business-hours-title">
              <div className="flex items-center gap-2 mb-4 text-amber-700">
                <Clock3 size={18} aria-hidden="true" />
                <h2 id="business-hours-title" className="text-xs font-bold uppercase tracking-wider">업무 시간</h2>
              </div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4"><dt className="text-stone-600">월요일 - 금요일</dt><dd className="font-semibold text-stone-900">{settings.business_hours.replace(/^평일\s*/, '')}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-stone-600">점심 시간</dt><dd className="font-semibold text-stone-900">{settings.lunch_hours}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-stone-600">토·일·공휴일</dt><dd className="font-semibold text-stone-900">휴무</dd></div>
              </dl>
            </section>
          </section>

          <section className="lg:col-span-3 flex flex-col gap-4" aria-label="오시는 길">
            <div className="relative rounded-lg overflow-hidden border border-stone-200" style={{ height: 480 }}>
              <div id="kakao-map" className="w-full h-full" />
              {status === 'loading' && (
                <div className="absolute inset-0 bg-stone-100 flex items-center justify-center text-stone-600">
                  <div className="text-center"><LoaderCircle className="animate-spin mx-auto mb-3 text-amber-600" size={30} /><p className="text-sm">지도를 불러오는 중...</p></div>
                </div>
              )}
              {status === 'error' && (
                <div className="absolute inset-0 bg-stone-100 flex items-center justify-center">
                  <div className="text-center p-8 max-w-sm">
                    <Map className="mx-auto mb-3 text-stone-500" size={36} aria-hidden="true" />
                    <p className="text-stone-700 font-semibold mb-1">지도를 표시할 수 없습니다</p>
                    <p className="text-stone-600 text-xs mb-4">관리자 사이트 설정에서 카카오 JavaScript 키와 사용 여부를 확인해 주세요.</p>
                    <a href={`https://map.kakao.com/link/search/${encodedAddress}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-700 text-white font-bold px-5 py-2.5 rounded-md text-sm">
                      <Map size={16} /> 카카오맵에서 보기
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <a href={`https://map.kakao.com/link/to/${encodedAddress},${lat},${lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-stone-900 font-bold py-3 rounded-md text-sm"><Car size={17} /> 카카오맵 길찾기</a>
              <a href={`https://map.naver.com/v5/search/${encodedAddress}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-md text-sm"><Map size={17} /> 네이버 지도</a>
              <a href={`https://map.kakao.com/link/search/${encodedAddress}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold py-3 rounded-md text-sm"><MapPin size={17} /> 주소 검색</a>
            </div>
          </section>
        </div>

        <section className="mt-16 bg-stone-900 rounded-xl p-10 text-center text-white">
          <h2 className="text-xl md:text-2xl font-bold mb-3">OEM/ODM 전문 제작 문의</h2>
          <p className="text-stone-300 mb-6 text-sm">가방 전문 제작, 소량 샘플부터 대량 생산까지 상담해 드립니다.</p>
          <Link to="/oem" className="inline-block bg-amber-500 text-black font-bold px-8 py-3 rounded-md hover:bg-amber-400">문의 폼 작성하기</Link>
        </section>
      </main>
    </div>
  );
}

function ContactItem({ icon: Icon, label, children }: { icon: React.ElementType; label: string; children: React.ReactNode }) {
  return (
    <section className="bg-stone-50 rounded-lg p-6 flex items-start gap-4 border border-stone-100">
      <div className="w-11 h-11 bg-stone-900 text-amber-400 rounded-md flex items-center justify-center flex-shrink-0"><Icon size={21} aria-hidden="true" /></div>
      <div className="min-w-0"><h2 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">{label}</h2>{children}</div>
    </section>
  );
}
